import { useEffect, useState } from 'react';
import { Package, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { apiFetch } from '../lib/api';

interface PhoneCaseOrder {
  id: number;
  orderNum: string;
  donationNum: string | null;
  donorName: string;
  donorPhone: string;
  phoneModel: string;
  illustrationChoice: string | null;
  colorPreference: string | null;
  sfLockerCode: string | null;
  sfLockerAddress: string | null;
  status: string;
  createdAt: string;
  adminNotes: string | null;
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-blue-100 text-blue-700',
  produced: 'bg-purple-100 text-purple-700',
  shipped: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

const STATUS_LABELS: Record<string, string> = {
  pending: '待確認',
  confirmed: '已確認',
  produced: '製作中',
  shipped: '已寄出',
  cancelled: '已取消',
};

export default function AdminPhoneCaseOrders() {
  const [orders, setOrders] = useState<PhoneCaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    const res = await apiFetch<{ orders: PhoneCaseOrder[] }>('/api/admin/phone-case-orders');
    setLoading(false);
    if (res.success && res.data) setOrders(res.data.orders);
  };

  const updateStatus = async (id: number, status: string) => {
    const res = await apiFetch(`/api/admin/phone-case-orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
    if (res.success) loadOrders();
  };

  const updateNotes = async (id: number, adminNotes: string) => {
    await apiFetch(`/api/admin/phone-case-orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ admin_notes: adminNotes }),
    });
  };

  const filtered = orders.filter(o =>
    (o.donorName + o.donorPhone + o.phoneModel + o.orderNum + (o.donationNum || '')).toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Package className="w-6 h-6 text-brand-600" />
          <h1 className="text-2xl font-bold">手機殼訂單</h1>
        </div>
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="搜尋..." className="px-4 py-2 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400 w-64" />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-600" /></div>
      ) : (
        <div className="space-y-3">
          {filtered.length === 0 && !loading && (
            <div className="text-center py-16 text-gray-400">
              <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>暫無手機殼訂單</p>
            </div>
          )}
          {filtered.map((o) => (
            <div key={o.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-4 flex items-center justify-between cursor-pointer" onClick={() => setExpandedId(expandedId === o.id ? null : o.id)}>
                <div className="flex items-center gap-4">
                  <span className="font-mono text-sm text-gray-400">{o.orderNum}</span>
                  {o.donationNum && (
                    <span className="font-mono text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded">{o.donationNum}</span>
                  )}
                  <span className="font-semibold text-gray-900">{o.donorName}</span>
                  <span className="text-sm text-gray-500">{o.phoneModel}</span>
                </div>
                <div className="flex items-center gap-3">
                  <select value={o.status} onChange={(e) => { e.stopPropagation(); updateStatus(o.id, e.target.value); }} className={`px-3 py-1 rounded-lg text-xs font-semibold outline-none cursor-pointer ${STATUS_COLORS[o.status] || 'bg-gray-100'}`}>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  {expandedId === o.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </div>
              </div>

              {expandedId === o.id && (
                <div className="px-4 pb-4 border-t border-gray-50 pt-3 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-400 text-xs mb-1">捐款編號</p>
                    <p className="font-medium font-mono">{o.donationNum || '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-1">訂單時間</p>
                    <p className="font-medium">{new Date(o.createdAt).toLocaleString('zh-HK')}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-1">電話</p>
                    <p className="font-medium">{o.donorPhone}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-1">插圖選擇</p>
                    <p className="font-medium">{o.illustrationChoice || '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs mb-1">顏色偏好</p>
                    <p className="font-medium">{o.colorPreference || '—'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-400 text-xs mb-1">順豐櫃</p>
                    <p className="font-medium">{o.sfLockerCode ? `${o.sfLockerCode} / ${o.sfLockerAddress || ''}` : '—'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-400 text-xs mb-1">備註</p>
                    <textarea defaultValue={o.adminNotes || ''} onBlur={(e) => updateNotes(o.id, e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none focus:border-brand-400 h-20 resize-none" placeholder="添加備註..." />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
